// Event buffer — batches analytics events and flushes to the ingestion API.
// Flush triggers: 10 events accumulated OR 30 seconds elapsed.
// Beacon API fallback on page unload.

import type { EventEnvelope, EventBatch } from "@cieda/types";

export const MAX_BATCH_SIZE = 10;
export const FLUSH_INTERVAL_MS = 30_000; // 30 seconds
const MAX_PAYLOAD_EVENTS = 50; // API limit per batch

export class AnalyticsBuffer {
  private buffer: EventEnvelope[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly endpoint: string;

  constructor(endpoint: string) {
    this.endpoint = endpoint;
    this.timer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
  }

  push(event: EventEnvelope): void {
    this.buffer = [...this.buffer, event];
    if (this.buffer.length >= MAX_BATCH_SIZE) {
      this.flush();
    }
  }

  async flush(): Promise<void> {
    if (this.buffer.length === 0) return;

    // Take up to MAX_PAYLOAD_EVENTS, leave the rest
    const toSend = this.buffer.slice(0, MAX_PAYLOAD_EVENTS);
    const remaining = this.buffer.slice(MAX_PAYLOAD_EVENTS);
    this.buffer = remaining;

    const batch: EventBatch = {
      events: toSend,
      sent_at: new Date().toISOString(),
    };

    try {
      const response = await fetch(this.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(batch),
      });

      if (!response.ok) {
        // Re-add events to buffer for retry
        this.buffer = [...toSend, ...this.buffer];
        console.warn("[analytics-buffer] Flush failed, events re-queued:", response.status);
      }
    } catch (error) {
      // Re-add events to buffer for retry
      this.buffer = [...toSend, ...this.buffer];
      console.warn("[analytics-buffer] Flush error, events re-queued:", error);
    }
  }

  /**
   * Uses navigator.sendBeacon for reliable delivery on page unload.
   * sendBeacon is fire-and-forget — no retry possible.
   */
  flushOnUnload(): void {
    if (this.buffer.length === 0) return;

    const batch: EventBatch = {
      events: this.buffer.slice(0, MAX_PAYLOAD_EVENTS),
      sent_at: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(batch)], {
      type: "application/json",
    });
    navigator.sendBeacon(this.endpoint, blob);
    this.buffer = [];
  }

  /**
   * Stops the flush timer. Call on cleanup.
   */
  destroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.flush();
  }

  get pendingCount(): number {
    return this.buffer.length;
  }
}
